import React, { useEffect, useState } from "react";
import { clientApi } from "../lib/api";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Circle, ArrowRight, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function Acompanhamento() {
  const [campaigns, setCampaigns] = useState([]);
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(params.get("campaign") || "");
  const [data, setData] = useState(null);

  useEffect(() => {
    clientApi.get("/campaigns").then(({ data }) => {
      setCampaigns(data);
      if (!selected && data.length) setSelected(data[0].id);
    });
  }, [selected]);

  useEffect(() => {
    if (!selected) return;
    clientApi.get(`/timeline/${selected}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [selected]);

  const approve = async (evId, approved) => {
    try {
      await clientApi.post(`/timeline/${evId}/approve`, { approved, notes: "" });
      toast.success(approved ? "Aprovado!" : "Reprovado registrado.");
      const { data } = await clientApi.get(`/timeline/${selected}`);
      setData(data);
    } catch { toast.error("Erro ao registrar aprovação"); }
  };

  return (
    <div className="space-y-6" data-testid="cliente-acompanhamento-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-accent-orange mb-1">Acompanhamento</div>
        <h1 className="font-display text-3xl md:text-4xl">Linha do Tempo da Campanha</h1>
        <p className="text-sm text-neutral-400 mt-2">Acompanhe cada etapa do projeto e valide os pontos-chave.</p>
      </div>

      <select value={selected} onChange={(e) => setSelected(e.target.value)} className="w-full md:w-96 bg-black border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="timeline-campaign-select">
        <option value="">Selecione uma campanha…</option>
        {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {data && (
        <div className="border border-white/10 rounded-xl p-6 md:p-8 bg-white/[0.02]">
          <div className="font-display text-2xl mb-1">{data.campaign.name}</div>
          <div className="text-xs text-neutral-500 mb-8">{data.campaign.period} · {data.campaign.status}</div>

          <ol className="relative border-l border-white/10 ml-3 space-y-4" data-testid="timeline-events">
            {data.events.map((ev, i) => {
              const approved = ev.approval_status === "approved";
              const rejected = ev.approval_status === "rejected";
              return (
                <motion.li key={ev.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className="ml-6 relative pb-4">
                  <span className={`absolute -left-9 top-1 flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-[#0B0B0B] ${approved ? "bg-green-500" : rejected ? "bg-red-500" : "bg-accent-orange"}`}>
                    {approved ? <CheckCircle2 className="w-4 h-4 text-black" /> : rejected ? <XCircle className="w-4 h-4 text-white" /> : <Circle className="w-3 h-3 text-black" fill="currentColor" />}
                  </span>
                  <div className="border border-white/10 rounded-lg p-4 hover:border-accent-orange transition-colors" data-testid={`timeline-event-${ev.id}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="font-semibold text-lg">{ev.title}</div>
                        <div className="text-[11px] text-neutral-500 mt-1">{ev.date && new Date(ev.date).toLocaleDateString("pt-BR")}</div>
                      </div>
                      {ev.requires_approval && !ev.approval_status && (
                        <div className="flex gap-2">
                          <button onClick={() => approve(ev.id, true)} data-testid={`approve-${ev.id}`} className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-black text-xs font-bold px-3 py-1.5 rounded-full transition">
                            <Check className="w-3 h-3" /> Aprovar
                          </button>
                          <button onClick={() => approve(ev.id, false)} data-testid={`reject-${ev.id}`} className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full transition">
                            <XIcon className="w-3 h-3" /> Reprovar
                          </button>
                        </div>
                      )}
                      {ev.approval_status === "approved" && <span className="text-xs bg-green-500/10 text-green-400 px-2 py-1 rounded-full">✓ Aprovado</span>}
                      {ev.approval_status === "rejected" && <span className="text-xs bg-red-500/10 text-red-400 px-2 py-1 rounded-full">✗ Reprovado</span>}
                    </div>
                    {ev.description && <p className="text-sm text-neutral-400 mt-3">{ev.description}</p>}
                    {ev.approval_status && ev.approval_at && (
                      <div className="text-[10px] text-neutral-500 mt-3">
                        {ev.approval_status === "approved" ? "Aprovado por" : "Reprovado por"} {ev.approval_by} em {new Date(ev.approval_at).toLocaleString("pt-BR")}
                      </div>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      )}

      {!data && selected && <div className="text-sm text-neutral-500 border border-dashed border-white/10 rounded-xl p-8 text-center">Sem eventos configurados nesta campanha.</div>}
    </div>
  );
}
